import React, { useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { MessageSquare } from "lucide-react";
import { useTheme } from "@mui/material/styles";
import { Button } from "../../components/Button";
import { Label } from "../../components/Label";
import { CustomTextField } from "../../components/CustomTextField";
import { UnifiedModal } from "../../components/UnifiedModal";
import apiClient from "../../api-client";
import { INTERNAL_THREADS_ENDPOINT } from "./threadUrlUtils";

// Sin `client` crea un hilo interno de la agencia del usuario
export function CreateThreadModal({ open, onClose, client, onSuccess }) {
  const theme = useTheme();
  const [title, setTitle] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const neutralBorderColor =
    theme.palette.border?.input || theme.palette.divider || "#d0d5dd";

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validación
    if (!title.trim()) {
      setError("El título del hilo es requerido");
      return;
    }

    if (title.trim().length < 3) {
      setError("El título debe tener al menos 3 caracteres");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const response = await apiClient.post(
        client ? "communication/threads/" : INTERNAL_THREADS_ENDPOINT,
        { title: title.trim(), ...(client && { client: client.id }) }
      );

      // Notificar éxito
      if (onSuccess) {
        onSuccess(response.data);
      }

      // Limpiar y cerrar
      setTitle("");
      onClose();
    } catch (err) {
      console.error("Error creando thread:", err);

      // Extraer mensaje de error
      let errorMessage = "Error al crear el hilo de conversación";
      if (err.response?.data) {
        const errorData = err.response.data;
        if (typeof errorData === "object") {
          const firstError = Object.values(errorData)[0];
          if (Array.isArray(firstError)) {
            errorMessage = firstError[0];
          } else if (typeof firstError === "string") {
            errorMessage = firstError;
          }
        } else if (typeof errorData === "string") {
          errorMessage = errorData;
        }
      }

      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setTitle("");
      setError("");
      onClose();
    }
  };

  return (
    <UnifiedModal open={open} onClose={handleClose} title={client ? `Nuevo hilo para el cliente: ${client.name}` : "Nuevo hilo interno"}>
      <Stack spacing={3}>
        {/* Información del cliente */}
        

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Título del hilo */}
            <Box>
              <Label sx={{ fontSize: "1rem" }}>Título del hilo *</Label>
              <CustomTextField
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (error) setError("");
                }}
                placeholder="Ej: Campaña Facebook Ads"
                required
                autoFocus
                disabled={isSubmitting}
                error={!!error}
                errorMessage={error}
                focusBorderColor={neutralBorderColor}
                sx={{ marginTop: "8px" }}
              />
              {!error && (
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: "0.75rem",
                    marginTop: "4px",
                    display: "block",
                  }}
                >
                  Mínimo 3 caracteres
                </Typography>
              )}
            </Box>

            {/* Footer buttons */}
            <Stack
              direction="row"
              justifyContent="flex-end"
              spacing={1.5}
              sx={{ paddingTop: "24px" }}
            >
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting || !title.trim()}>
                {isSubmitting ? (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box
                      sx={{
                        width: "16px",
                        height: "16px",
                        marginRight: "8px",
                        border: "1px solid currentColor",
                        borderTop: "1px solid transparent",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                        "@keyframes spin": {
                          "0%": { transform: "rotate(0deg)" },
                          "100%": { transform: "rotate(360deg)" },
                        },
                      }}
                    />
                    Creando...
                  </Box>
                ) : (
                  <>
                    <MessageSquare size={16} style={{ marginRight: "8px" }} />
                    Crear Hilo
                  </>
                )}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </UnifiedModal>
  );
}
